import axios from "axios";
import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
} from "@mui/material";
import {
  getBasicFormatSections,
  getReportSectionNames,
  getReportSections,
  getUserName,
} from "../../../../common/EntitiesManagement";
import localize from "../../../../common/localize";
import ModalBackground from "../../../../components/common/ModalBackground";
import CheckboxComponent from "../../../../components/common/CheckboxComponent";
import Combobox from "../../../../components/common/Combobox";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import { encryptForUrl } from "../../../../common/utils";
import {
  getInputLabelProps,
  getInputProps,
} from "../../../../common/appStyles";
import PageTitleSubtitle from "../../../../components/PageTitleSubTitle";

export default function ReportSetup({
  locationHref,
  showGenerateInformModal,
  setShowGenerateInformModal,
  editionMode,
  title,
  reportSetupModel,
  setReportSetupModel,
  isReadOnly,
}) {
  const { REPORT_TYPE_ENDPOINT, config } = useGlobalContext();
  const reportSectionNames = getReportSectionNames();
  const [reportTypes, setReportTypes] = useState([]);
  const [reportSections, setReportSections] = useState(
    reportSetupModel?.ReportSections ?? getBasicFormatSections(),
  );
  const [generateOriginal, setGenerateOriginal] = useState(false);
  const [printFormat, setPrintFormat] = useState({
    Id: 0,
    Name: localize.service_order,
  });

  const getReportTypes = async () => {
    const result = await axios.get(
      `${REPORT_TYPE_ENDPOINT}/${getUserName()}`,
      config,
    );

    if (result.data?.Ok && result.data?.Data) {
      setReportTypes(result.data.Data);
    }
  };

  useEffect(() => {
    if (!editionMode) {
      getReportTypes();
    }
  }, []);

  const handleCheckedChanged = (e) => {
    switch (e.target.name) {
      case "rbtn_current_report":
        setGenerateOriginal(!e.target.checked);
        break;
      case "rbtn_original_report":
        setGenerateOriginal(e.target.checked);
        break;
      default:
        break;
    }
  };

  const handleSectionChange = (e) => {
    setReportSections({
      ...reportSections,
      [e.target.name]: e.target.checked,
    });

    if (editionMode) {
      setReportSetupModel({
        ...reportSetupModel,
        ReportSections: {
          ...reportSections,
          [e.target.name]: e.target.checked,
        },
      });
    }
  };

  const handleAllSectionsChange = (e) => {
    const newSections = Object.fromEntries(
      Object.entries(reportSections)
        .map(([key, val]) => [
          key,
          typeof val === "boolean" ? e.target.checked : val,
        ])
        .filter(([key, _]) => key != "hideModificationDate"),
    );

    setReportSections({
      ...newSections,
    });
  };

  const options = () => {
    const result = [
      {
        Id: 0,
        Name: localize.service_order,
        Default: true,
      },
      {
        Id: 1,
        Name: localize.report,
        Default: true,
      },
    ];

    if (reportTypes?.length > 0) {
      result.push(
        ...reportTypes.map((type) => ({
          Id: type.Id,
          Name: type.Name,
          Default: false,
        })),
      );
    }

    return result;
  };

  useEffect(() => {
    if (!editionMode) {
      switch (printFormat?.Id) {
        case 0:
          setReportSections(getBasicFormatSections());
          break;
        default:
          if (printFormat.Default) {
            setReportSections(getReportSections(printFormat?.Id));
          } else {
            const foundIndex = reportTypes.findIndex(
              (type) => type.Id === printFormat?.Id,
            );

            if (foundIndex >= 0) {
              setReportSections(
                JSON.parse(reportTypes[foundIndex].ReportSections),
              );
            }
          }

          break;
      }
    }
  }, [printFormat]);

  const handlePrintFormatChange = (value) => {
    if (!value) {
      setPrintFormat({
        Id: 0,
        Name: localize.service_order,
        Default: true,
      });
      return;
    }

    setPrintFormat(value);
  };

  const areAllSectionsSelected = () => {
    const entries = Object.entries(reportSections).filter(
      ([key, val]) => typeof val === "boolean" && key != "hideModificationDate",
    );
    return entries.every(([_, val]) => val === true);
  };

  const hideModificationDataCheckedChanged = (e) => {
    if (!e) {
      setReportSections({
        ...reportSections,
        hideModificationDate: false,
      });
    } else {
      setReportSections({
        ...reportSections,
        hideModificationDate: e.target.checked,
      });
    }

    if (editionMode) {
      setReportSetupModel({
        ...reportSetupModel,
        ReportSections: {
          ...reportSections,
          hideModificationDate: !e ? false : e.target.checked,
        },
      });
    }
  };

  const getEditionComponents = () => {
    return (
      <div className="w-full" style={{ marginTop: "10px" }}>
        <Box className="w-full flex items-center gap-2">
          <Box sx={{ width: "50%" }}>
            <label className="report_little_text">
              <b>{localize.report_sections}</b>
            </label>
          </Box>

          <Box sx={{ marginLeft: "5px" }}>
            <CheckboxComponent
              isReadOnly={isReadOnly}
              onValueChanged={handleAllSectionsChange}
              propertyName={"All"}
              text={localize.select_all}
              value={areAllSectionsSelected()}
            />
          </Box>
        </Box>

        <Divider />

        <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
          {reportSectionNames.map((section) => (
            <Grid item xs={6} key={Math.random()}>
              <CheckboxComponent
                isReadOnly={isReadOnly}
                onValueChanged={handleSectionChange}
                propertyName={section.name}
                text={section.title}
                value={reportSections[section.name]}
              />
            </Grid>
          ))}
        </Grid>

        <Box className="flex flex-col gap-2" style={{ marginTop: "10px" }}>
          <label className="report_little_text">
            <b>{localize.additional_setup}</b>
          </label>
          <Divider />

          <Box className="flex gap-2 mt-2 w-full items-center">
            <label className="report_little_text" style={{ width: "50%" }}>
              <PageTitleSubtitle
                title={localize.use_contractor_header}
                subtitle={localize.use_contractor_header_description}
                useEventTitleStyle
                useFieldTitleStyle
              />
            </label>

            <Box sx={{ width: "50%" }}>
              <FormControlLabel
                sx={{
                  "& .MuiFormControlLabel-label": {
                    fontSize: "12px",
                  },
                }}
                control={
                  <Switch
                    key={"useContractorHeader"}
                    name={"useContractorHeader"}
                    checked={reportSections?.useContractorHeader}
                    onChange={handleSectionChange}
                    slotProps={{ input: { "aria-label": "controlled" } }}
                  />
                }
                label={
                  reportSections?.useContractorHeader
                    ? localize.contractor
                    : localize.general
                }
              />
            </Box>
          </Box>

          <Box className="flex flex-col gap-2 w-full items-center">
            <Box className="flex gap-2 mt-2 w-full items-center">
              <label className="report_little_text" style={{ width: "50%" }}>
                <PageTitleSubtitle
                  title={localize.evidence_columns}
                  subtitle={localize.evidence_columns_description}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </label>

              <TextField
                type="number"
                InputLabelProps={getInputLabelProps()}
                inputProps={getInputProps()}
                value={reportSections.columns ?? 3}
                onChange={handlePanelReportColumnsChanged}
                size="small"
                id="reportSections_columns"
                name="reportSections_columns"
                variant="outlined"
              />
            </Box>

            <Box className="flex gap-2 mt-2 w-full items-center">
              <label className="report_little_text" style={{ width: "50%" }}>
                <PageTitleSubtitle
                  title={localize.form_results_format}
                  subtitle={localize.form_results_format_description}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </label>

              <Box sx={{ width: "50%" }}>
                <Combobox
                  id="formResultType"
                  fieldLabel={""}
                  fieldOptions={getFormResultsModeOptions()}
                  fieldValue={reportSections?.form_results_format}
                  handleValueChange={handleFormResultsFormatChange}
                  customSx={{ width: "180px" }}
                />
              </Box>
            </Box>

            <Box className="flex gap-2 mt-2 w-full items-center">
              <label className="report_little_text" style={{ width: "50%" }}>
                <PageTitleSubtitle
                  title={localize.hide_modification_data}
                  subtitle={localize.hide_modification_data_description}
                  useEventTitleStyle
                  useFieldTitleStyle
                />
              </label>

              <Box sx={{ width: "50%" }}>
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "12px",
                    },
                  }}
                  control={
                    <Switch
                      key={"hideModificationDate"}
                      checked={reportSections?.hideModificationDate}
                      onChange={hideModificationDataCheckedChanged}
                      slotProps={{ input: { "aria-label": "controlled" } }}
                    />
                  }
                  label={
                    reportSections?.hideModificationDate
                      ? localize.hidden
                      : localize.visible
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </div>
    );
  };

  const handlePanelReportColumnsChanged = (e) => {
    if (!e.target.value) {
      setReportSections({
        ...reportSections,
        columns: 3,
      });
    } else {
      setReportSections({
        ...reportSections,
        columns:
          e.target.value < 1 ? 1 : e.target.value > 3 ? 3 : e.target.value,
      });
    }

    if (editionMode) {
      setReportSetupModel({
        ...reportSetupModel,
        ReportSections: {
          ...reportSections,
          columns: !e
            ? 3
            : e.target.value < 1
            ? 1
            : e.target.value > 3
            ? 3
            : e.target.value,
        },
      });
    }
  };

  const getFormResultsModeOptions = () => [
    {
      Id: 0,
      Name: localize.form_results_two_columns,
    },
    {
      Id: 1,
      Name: localize.form_results_single_column,
    },
  ];

  const handleFormResultsFormatChange = (value) => {
    if (!value) {
      setReportSections({
        ...reportSections,
        form_results_format: {
          Id: 0,
          Name: localize.form_results_two_columns,
        },
      });
    } else {
      setReportSections({
        ...reportSections,
        form_results_format: {
          ...value,
        },
      });
    }

    if (editionMode) {
      setReportSetupModel({
        ...reportSetupModel,
        ReportSections: {
          ...reportSections,
          form_results_format: !value
            ? {
                Id: 0,
                Name: localize.form_results_two_columns,
              }
            : {
                ...value,
              },
        },
      });
    }
  };

  return editionMode ? (
    getEditionComponents()
  ) : (
    <ModalBackground
      isDrawerMode
      showModal={showGenerateInformModal}
      setShowModal={setShowGenerateInformModal}
      title={title ?? localize.print_service_order}
      okButtonText={localize.generate}
      onClickToSubmit={() => {
        setShowGenerateInformModal(false);
        const locationSplit = locationHref.split("?");
        window.open(
          `${
            locationSplit[0]
          }/report/${generateOriginal}?sections=${encryptForUrl(
            JSON.stringify(reportSections),
          )}`,
          "_blank",
        );
        setGenerateOriginal(false);
      }}
      onClickToCancel={() => {
        setShowGenerateInformModal(false);
        setGenerateOriginal(false);
      }}
      Component={
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          className="mt-2"
        >
          {!editionMode && (
            <FormControl required fullWidth>
              <Combobox
                id="reportype"
                fieldLabel={localize.format}
                fieldOptions={options()}
                fieldValue={printFormat}
                handleValueChange={handlePrintFormatChange}
              />
            </FormControl>
          )}

          {(editionMode || printFormat?.Id > 0) && (
            <div style={{ paddingTop: "10px" }}>
              {!editionMode && (
                <Box className="flex flex-col gap-2">
                  <label className="report_little_text">
                    <b>{localize.report_types}</b>
                  </label>
                  <Divider />
                  <Box className="flex flex-col gap-2">
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="false"
                        name="radio-buttons-group-report-type"
                      >
                        <FormControlLabel
                          name="rbtn_current_report"
                          value="false"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.generate_current_report_with_changes}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />
                        <FormControlLabel
                          name="rbtn_original_report"
                          value="true"
                          control={<Radio />}
                          label={
                            <span style={{ fontSize: "12px" }}>
                              {localize.generate_original_report}
                            </span>
                          }
                          onChange={handleCheckedChanged}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                </Box>
              )}

              {getEditionComponents()}
            </div>
          )}
        </div>
      }
    />
  );
}
